// Single source of truth for reusable Zod field schemas + patterns.
import { z } from "zod";

/**
 * Indian mobile number: an optional +91 / 91 / 0 prefix followed by a
 * 10-digit number starting with 6-9.
 */
export const INDIA_PHONE_REGEX = /^(?:\+91|91|0)?[6-9]\d{9}$/;

/** Required Indian phone number. Override the message per usage if needed. */
export const phoneSchema = (message = "Please enter a valid phone number") =>
    z.string({ error: message }).regex(INDIA_PHONE_REGEX, { error: message });

/** Optional Indian phone — accepts `undefined` or an empty string. */
export const optionalPhoneSchema = (message?: string) =>
    phoneSchema(message).optional().or(z.literal(""));

/** Required email address. */
export const emailSchema = (message = "Please enter a valid email") =>
    z.email({ error: message });

/** Optional email — accepts `undefined` or an empty string. */
export const optionalEmailSchema = (message?: string) =>
    emailSchema(message).optional().or(z.literal(""));

/**
 * Image upload constraints — single source of truth for every image upload
 * (member photos, staff photos, gym logo, etc.). Import these instead of
 * re-declaring size/MIME limits anywhere.
 */
export const MAX_IMAGE_FILE_SIZE = 1024 * 1024 * 3; // 3MB
export const MAX_IMAGE_FILE_SIZE_MB = MAX_IMAGE_FILE_SIZE / 1024 / 1024;
export const ACCEPTED_IMAGE_MIME_TYPES = [
    "image/jpeg",
    "image/jpg",
    "image/png",
    "image/webp",
];

/** Validates a single uploaded image `File` (type + size). */
export const imageFileSchema = z
    .custom<File>((v) => v instanceof File, "Please select an image")
    .refine(
        (f) => f.size <= MAX_IMAGE_FILE_SIZE,
        `Max file size is ${MAX_IMAGE_FILE_SIZE_MB}MB`,
    )
    .refine(
        (f) => ACCEPTED_IMAGE_MIME_TYPES.includes(f.type),
        ".jpg, .jpeg, .png and .webp files are accepted",
    );
